import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { CHAVE_DAS_FOTOS_DA_EQUIPE } from "@/lib/api/ficha-da-equipe";
import type { FotosDaEquipe, Session } from "@/lib/types";

/** userId → a URL da foto (já com o `?v=`), de quem tem foto. */
function porPessoa(lista: FotosDaEquipe): Map<string, string> {
  return new Map(lista.items.map((item) => [item.userId, item.photoUrl]));
}

/**
 * TUT-558 — a foto de uma pessoa da equipe para os avatares fora de Usuários
 * (o responsável do prazo), ou `null` para ficar nas iniciais.
 *
 * Desde a decisão do dono de 29/10, a foto de quem é da equipe interna é
 * vista por toda a equipe: a fonte é a lista das fotos (`GET /equipe/fotos`),
 * uma leitura para todos os avatares da tela, a mesma para a administração e
 * para quem não administra (a TUT-552 lia a equipe inteira, só da
 * administração, e a Minha ficha para os demais).
 *
 * Quem acessa pelo portal não tem lista da equipe: lá a foto só aparece em
 * quem respondeu na conversa com o escritório (`urlDaFotoNoPortal`, no
 * `ChatPanel`). Quem está sem grupo não alcança a lista (403). Nada é pedido
 * sem sessão no cache.
 */
export function useFotoDaPessoa(userId: string | null | undefined): string | null {
  // TUT-557 — a sessão é OBSERVADA, não lida uma vez: o avatar que monta antes
  // dela chegar ao cache refaz o cálculo quando ela chega. `enabled: false`
  // só assina o cache (quem busca a sessão é o portão, `App.tsx`).
  const { data: sessao } = useQuery<Session>({
    queryKey: ["session"],
    queryFn: () => api.session(),
    enabled: false,
  });
  const daEquipe = Boolean(sessao) && sessao?.user.roleKey !== "EXTERNAL" && !sessao?.semGrupo;
  const fotos = useQuery({
    queryKey: CHAVE_DAS_FOTOS_DA_EQUIPE,
    queryFn: () => api.fotosDaEquipe(),
    enabled: daEquipe,
    retry: false,
    staleTime: 5 * 60_000,
    select: porPessoa,
  });
  if (!userId) return null;
  return fotos.data?.get(userId) ?? null;
}
